import { authFetch } from '@/lib/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Edit3, Save, X, Plus, Trash2, Loader2, ChevronUp, ChevronDown, ImagePlus, Crop } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import ImageCropDialog from '@/components/ImageCropDialog';
import MiniRichText from '@/components/MiniRichText';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GuideHaccpStandPage() {
    const navigate = useNavigate();
    const isAdmin = JSON.parse(localStorage.getItem('guide_user') || '{}').role === 'admin';
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editSections, setEditSections] = useState([]);
    const [editDescription, setEditDescription] = useState('');
    const [editImageHeight, setEditImageHeight] = useState(200);
    const [editTitle, setEditTitle] = useState('');
    const [editSubtitle, setEditSubtitle] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);

    const fetchPage = useCallback(async () => {
        try {
            const res = await authFetch(`${API}/api/guide/pages/haccp-stand`);
            const data = await res.json();
            setPage(data);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    const sections = page?.sections || [];
    const descriptionImage = page?.description_image;

    const startEdit = () => {
        setEditSections(JSON.parse(JSON.stringify(sections)));
        setEditDescription(page?.description || '');
        setEditImageHeight(page?.image_height || 200);
        setEditTitle(page?.title || 'HACCP Stand');
        setEditSubtitle(page?.subtitle || 'Obligations HACCP du stand');
        setEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await authFetch(`${API}/api/guide/pages/haccp-stand`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sections: editSections, description: editDescription, image_height: editImageHeight, title: editTitle, subtitle: editSubtitle }),
            });
            const data = await res.json();
            setPage(data);
            setEditing(false);
            toast.success('Page mise a jour');
        } catch { toast.error('Erreur'); }
        finally { setSaving(false); }
    };

    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setCropImageSrc(reader.result);
            setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCropExistingImage = () => {
        if (descriptionImage) {
            setCropImageSrc(`${API}${descriptionImage}`);
            setCropDialogOpen(true);
        }
    };

    const handleCropped = async (file) => {
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await authFetch(`${API}/api/guide/pages/haccp-stand/image`, {
                method: 'POST',
                body: formData,
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setPage(prev => ({ ...prev, description_image: data.image_url + '?t=' + Date.now() }));
            toast.success('Illustration mise a jour');
        } catch { toast.error("Erreur lors de l'upload"); }
        finally { setUploading(false); }
    };

    const handleImageDelete = async () => {
        try {
            await authFetch(`${API}/api/guide/pages/haccp-stand/image`, { method: 'DELETE' });
            setPage(prev => ({ ...prev, description_image: null }));
            toast.success('Illustration supprimee');
        } catch { toast.error('Erreur'); }
    };

    const addSection = () => {
        setEditSections(prev => [...prev, { id: '', title: '', items: [] }]);
    };
    const removeSection = (idx) => {
        setEditSections(prev => prev.filter((_, i) => i !== idx));
    };
    const updateSectionTitle = (idx, value) => {
        setEditSections(prev => { const c = [...prev]; c[idx] = { ...c[idx], title: value }; return c; });
    };
    const moveSection = (idx, dir) => {
        setEditSections(prev => {
            const c = [...prev];
            const target = idx + dir;
            if (target < 0 || target >= c.length) return c;
            [c[idx], c[target]] = [c[target], c[idx]];
            return c;
        });
    };
    const addItem = (sIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items.push('');
            return c;
        });
    };
    const updateItem = (sIdx, iIdx, value) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items[iIdx] = value;
            return c;
        });
    };
    const removeItem = (sIdx, iIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items.splice(iIdx, 1);
            return c;
        });
    };
    const moveItem = (sIdx, iIdx, dir) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            const target = iIdx + dir;
            if (target < 0 || target >= c[sIdx].items.length) return c;
            [c[sIdx].items[iIdx], c[sIdx].items[target]] = [c[sIdx].items[target], c[sIdx].items[iIdx]];
            return c;
        });
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    if (editing) {
        return (
            <div className="px-4 py-5" data-testid="guide-haccp-stand-edit">
                <div className="space-y-1.5 mb-3">
                    <Input
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        placeholder="Titre..."
                        className="text-base font-bold h-9"
                        data-testid="edit-title-input"
                    />
                    <Input
                        value={editSubtitle}
                        onChange={e => setEditSubtitle(e.target.value)}
                        placeholder="Sous-titre..."
                        className="text-sm text-slate-500 h-8"
                        data-testid="edit-subtitle-input"
                    />
                </div>
                <div className="space-y-3">
                    <div className="bg-white rounded-xl border border-teal-200 p-3 space-y-2">
                        <label className="text-xs font-semibold text-teal-700">Presentation du stand</label>
                        {descriptionImage && (
                            <div className="relative">
                                <img src={`${API}${descriptionImage}`} alt="Illustration" className="w-full rounded-lg object-cover" style={{ maxHeight: `${editImageHeight}px` }} />
                                <button
                                    onClick={handleImageDelete}
                                    className="absolute top-2 right-2 bg-white/90 hover:bg-red-50 border border-slate-200 rounded-full p-1.5 shadow-sm"
                                    data-testid="delete-image-btn"
                                >
                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                </button>
                            </div>
                        )}
                        {descriptionImage && (
                            <div className="flex items-center gap-2">
                                <label className="text-xs text-slate-500 whitespace-nowrap">Hauteur</label>
                                <input
                                    type="range"
                                    min="80"
                                    max="500"
                                    value={editImageHeight}
                                    onChange={e => setEditImageHeight(Number(e.target.value))}
                                    className="flex-1 h-1.5 accent-teal-600"
                                    data-testid="image-height-slider"
                                />
                                <Input
                                    type="number"
                                    min={80}
                                    max={500}
                                    value={editImageHeight}
                                    onChange={e => setEditImageHeight(Number(e.target.value) || 80)}
                                    className="w-16 h-7 text-xs text-center"
                                    data-testid="image-height-input"
                                />
                                <span className="text-xs text-slate-400">px</span>
                            </div>
                        )}
                        <div className={descriptionImage ? "grid grid-cols-2 gap-2" : ""}>
                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelected} className="hidden" id="desc-image-upload" />
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-xs"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="upload-image-btn"
                            >
                                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}
                                {descriptionImage ? "Changer" : 'Ajouter une illustration'}
                            </Button>
                            {descriptionImage && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full text-xs"
                                    onClick={handleCropExistingImage}
                                    data-testid="crop-existing-image-btn"
                                >
                                    <Crop className="w-3.5 h-3.5 mr-1" /> Rogner
                                </Button>
                            )}
                        </div>
                        <textarea
                            value={editDescription}
                            onChange={e => setEditDescription(e.target.value)}
                            placeholder="Description du stand, obligations, contexte..."
                            className="w-full text-sm border border-slate-200 rounded-lg p-2 min-h-[80px] resize-y"
                        />
                    </div>
                    {editSections.map((section, si) => (
                        <div key={si} className="bg-white rounded-xl border border-slate-200 p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <Input value={section.title} onChange={e => updateSectionTitle(si, e.target.value)} placeholder="Categorie..." className="h-8 text-sm font-semibold flex-1" />
                                <button onClick={() => moveSection(si, -1)} disabled={si === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                                <button onClick={() => moveSection(si, 1)} disabled={si === editSections.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                                <button onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <div className="space-y-1 pl-2">
                                {(section.items || []).map((item, ii) => (
                                    <div key={ii} className="flex items-start gap-1">
                                        <div className="flex-1">
                                            <MiniRichText value={item} onChange={val => updateItem(si, ii, val)} placeholder="Item..." />
                                        </div>
                                        <div className="flex items-center gap-0.5 mt-1">
                                            <button onClick={() => moveItem(si, ii, -1)} disabled={ii === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                                            <button onClick={() => moveItem(si, ii, 1)} disabled={ii === (section.items || []).length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                                            <button onClick={() => removeItem(si, ii)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                        </div>
                                    </div>
                                ))}
                                <Button variant="ghost" size="sm" className="text-xs text-slate-400 w-full" onClick={() => addItem(si)}>
                                    <Plus className="w-3 h-3 mr-1" />Item
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full" onClick={addSection}>
                        <Plus className="w-4 h-4 mr-1" />Categorie
                    </Button>
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1" />Annuler</Button>
                        <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleSave} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Sauvegarder
                        </Button>
                    </div>
                </div>

                <ImageCropDialog
                    open={cropDialogOpen}
                    onClose={() => setCropDialogOpen(false)}
                    imageSrc={cropImageSrc}
                    onCropped={handleCropped}
                    aspect={16 / 9}
                />
            </div>
        );
    }

    return (
        <div className="px-4 py-5" data-testid="guide-haccp-stand">
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </button>

            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-900">{page?.title || 'HACCP Stand'}</h1>
                    <p className="text-sm text-slate-500">{page?.subtitle || 'Obligations HACCP du stand'}</p>
                </div>
                {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400 p-1" onClick={startEdit} data-testid="edit-haccp-stand-btn">
                    <Edit3 className="w-3.5 h-3.5" />
                </Button>}
            </div>

            <div className="space-y-4">
                {(page?.description || descriptionImage) && (
                    <div className="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden p-4">
                        {descriptionImage && (
                            <img
                                src={`${API}${descriptionImage}`}
                                alt="Illustration du stand"
                                className="float-left rounded-lg object-cover mr-3 mb-2"
                                style={{ maxHeight: `${page?.image_height || 200}px`, maxWidth: '45%' }}
                            />
                        )}
                        {page?.description && (
                            <p className="text-sm text-teal-800 leading-relaxed whitespace-pre-wrap">{page.description}</p>
                        )}
                        <div className="clear-both" />
                    </div>
                )}
                {sections.map((section) => (
                    <div key={section.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="bg-teal-50 px-4 py-2.5 border-b border-teal-100">
                            <h2 className="font-semibold text-teal-800 text-sm">{section.title}</h2>
                        </div>
                        <ul className="divide-y divide-slate-100">
                            {(section.items || []).map((item, j) => (
                                <li key={j} className="px-4 py-1.5 text-sm text-slate-600 flex gap-2">
                                    <span className="text-teal-500 font-bold mt-0.5 flex-shrink-0">-</span>
                                    <span dangerouslySetInnerHTML={{ __html: item }} />
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                {sections.length === 0 && !page?.description && !descriptionImage && (
                    <p className="text-sm text-slate-400 text-center py-8">Aucun contenu. Cliquez sur Modifier pour ajouter du contenu.</p>
                )}
            </div>
        </div>
    );
}
